import type { ReactNode } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { LinkTabs, type LinkTab } from "@/components/ui/link-tabs";
import type { ContributorWorkspaceCopy } from "./workspace-copy.types";
import { COLLECTION_ICONS, COLLECTION_SECTIONS, collectionHref } from "./workspace-destinations";

/**
 * Earnings, contracts and, for a collector, the collection pages. They belong
 * to the contributor's own space: same header as the rest of the app, the
 * sub-views as tabs in the page (no back-office sidebar).
 */
export function ContributorWorkspaceShell({ copy, children }: { copy: ContributorWorkspaceCopy; children: ReactNode }) {
  const { lang = "fr" } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const base = `/${lang}`;
  const inCollection = pathname.startsWith(`${base}/collector`);

  const earningsTabs: LinkTab[] = [
    { key: "overview", label: copy.overview, href: `${base}/get-paid?section=overview`, icon: "layoutDashboard", match: [`${base}/get-paid`] },
    { key: "test", label: copy.test, href: `${base}/get-paid?section=test`, icon: "fileText" },
    { key: "requests", label: copy.requests, href: `${base}/get-paid?section=requests`, icon: "wallet" },
    { key: "contracts-pending", label: copy.pendingContracts, href: `${base}/user/contracts?tab=pending`, icon: "edit", match: [`${base}/user/contracts`] },
    { key: "contracts-signed", label: copy.signedContracts, href: `${base}/user/contracts?tab=signed`, icon: "checkCircle2" },
  ];

  const collectionTabs: LinkTab[] = copy.collection
    ? COLLECTION_SECTIONS.map((section) => ({
        key: `collection-${section}`,
        label: copy.collection!.sections[section].label,
        href: collectionHref(base, section),
        icon: COLLECTION_ICONS[section],
        // The bare collector URL opens its dashboard.
        match: section === "dashboard" ? [`${base}/collector`] : undefined,
      }))
    : [];

  // Each space keeps its own tabs: Collecte and Gains are both in the header,
  // a tab that jumps to the other space (and its other tabs) only lost people.
  const tabs = inCollection ? collectionTabs : earningsTabs;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-6xl space-y-6 px-4 pb-16 pt-6 sm:px-6 lg:px-8">
        <LinkTabs label={inCollection && copy.collection ? copy.collection.label : copy.earnings} tabs={tabs} />
        {children}
      </div>
    </Layout>
  );
}
